import { motion } from "framer-motion";
import { Home, MapPinned, Hammer, Building2, Car, CarFront, Truck, ArrowRight } from "lucide-react";
import { useChat } from "@/context/ChatContext";

const EASE = [0.22, 1, 0.36, 1];

function CategoryCard({ icon: Icon, title, text }) {
  return (
    <div className="group flex h-full flex-col rounded-3xl border border-slate-200/80 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-brand-900/5">
      <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-50 text-brand-600 transition-colors group-hover:bg-brand-600 group-hover:text-white">
        <Icon className="h-5 w-5" />
      </span>
      <h4 className="mt-4 font-display text-base font-bold text-slate-900">{title}</h4>
      <p className="mt-1.5 text-base leading-relaxed text-slate-600">{text}</p>
    </div>
  );
}

function Chip({ children }) {
  return (
    <span className="rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-bold text-slate-600">
      {children}
    </span>
  );
}

export default function Categories() {
  const { openChat } = useChat();

  return (
    <section className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-6">
        <div id="imoveis" className="scroll-mt-24 grid grid-cols-1 items-start gap-10 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="lg:col-span-4"
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-600">Consórcio de imóvel</p>
            <h3 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              A casa própria, planejada do jeito certo
            </h3>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Da primeira casa ao investimento, a carta de crédito de imóvel dá poder de compra à vista
              para negociar melhor — inclusive para construir ou reformar.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Chip>Primeira casa</Chip>
              <Chip>Investimento</Chip>
              <Chip>Reforma</Chip>
            </div>
            <div className="mt-6 overflow-hidden rounded-3xl">
              <img
                src="https://images.pexels.com/photos/1974596/pexels-photo-1974596.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Residência moderna com jardim"
                className="h-44 w-full object-cover"
                loading="lazy"
              />
            </div>
            <button
              data-testid="category-imovel-cta"
              onClick={() => openChat("imovel")}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-600 px-6 py-3.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-700 hover:shadow-lg"
            >
              Quero um imóvel <ArrowRight className="h-4 w-4" />
            </button>
          </motion.div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:col-span-8">
            <CategoryCard icon={Home} title="Residencial" text="Sua casa própria para morar — a conquista que mais muda a vida de uma família." />
            <CategoryCard icon={MapPinned} title="Terreno" text="Compre o terreno agora e construa no seu tempo, sem pressa e sem juros." />
            <CategoryCard icon={Hammer} title="Construção ou reforma" text="Use a carta de crédito para colocar a obra de pé ou dar um upgrade na sua casa." />
            <CategoryCard icon={Building2} title="Imóvel comercial" text="Ponto comercial ou imóvel para renda: o consórcio também planeja o seu negócio." />
          </div>
        </div>

        <div id="veiculos" className="mt-24 scroll-mt-24 grid grid-cols-1 items-start gap-10 lg:grid-cols-12">
          <div className="order-2 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:order-1 lg:col-span-8 lg:grid-cols-3">
            <CategoryCard icon={Car} title="Carro novo" text="0 km, com garantia de fábrica: comece o planejamento antes de escolher o modelo." />
            <CategoryCard icon={CarFront} title="Carro seminovo" text="Custo-benefício inteligente para trocar de carro sem apertar o orçamento." />
            <CategoryCard icon={Truck} title="SUV ou utilitário" text="Espaço para a família ou força para o trabalho — o utilitário certo para a sua rotina." />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="order-1 lg:order-2 lg:col-span-4"
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-600">Consórcio de veículo</p>
            <h3 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              O carro da família (ou do trabalho) no seu ritmo
            </h3>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Prazos mais curtos e planejamento sob medida: na contemplação, você compra à vista e
              negocia melhor — novo ou seminovo, para a família ou para ganhar a vida.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Chip>Trabalho</Chip>
              <Chip>Família</Chip>
              <Chip>0 km ou usado</Chip>
            </div>
            <div className="mt-6 overflow-hidden rounded-3xl">
              <img
                src="https://images.pexels.com/photos/2920064/pexels-photo-2920064.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="SUV em estrada"
                className="h-44 w-full object-cover"
                loading="lazy"
              />
            </div>
            <button
              data-testid="category-veiculo-cta"
              onClick={() => openChat("veiculo")}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-600 px-6 py-3.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-700 hover:shadow-lg"
            >
              Quero um veículo <ArrowRight className="h-4 w-4" />
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
